// Drawing for the historical dock's two text layouts: LABELS under the rows layout's bubbles, and the BAR CHART layout. Plain DOM (no d3, no clusterService), so it can be tested in jsdom and cannot
// make clusterService import itself. Every write is made only when the value changed (docs/PERFORMANCE_120HZ.md: a position or attribute is never rewritten for nothing), because the dock is
// re-laid-out whenever a count moves and live traffic moves counts all the time.
import { BarListSlot, BarListLayout, barLength } from "./historicalBarList";
import { fitLabel, labelWidthIn, Measure } from "./historicalLabels";
import { COLUMN_METRICS, ColumnsLayout } from "./historicalColumns";
import { DONUT_OTHER_ID, DonutLayout } from "./historicalDonut";

const NS = "http://www.w3.org/2000/svg";
export const LABELS_LAYER_ID = "historical-labels";
export const LIST_LAYER_ID = "historical-list";
export const CHART_ON_CLASS = "historical-chart-on";
/** the gap between the end of a bar and its count */
export const BAR_VALUE_GAP = 6;

/** sets an attribute only when it is not already that value */
const setAttr = (el: Element, name: string, value: string | number): void => {
  const text = String(value);
  if (el.getAttribute(name) !== text) el.setAttribute(name, text);
};

// what was last written, per element and property, so an identical draw writes nothing (the value is NOT read back from the element: not every engine keeps every property, and a read costs style work)
const styled = new WeakMap<Element, Map<string, string>>();
/** sets a CSS property only when it is not already what was last set. The bars' geometry and their count's position are set as CSS properties (and, for the bars, the attributes too) because only CSS can TRANSITION them: when a bubble lands in a bar, the bar grows instead of jumping. */
const setStyle = (el: Element, name: string, value: string): void => {
  let written = styled.get(el);
  if (!written) styled.set(el, (written = new Map()));
  if (written.get(name) === value) return;
  written.set(name, value);
  (el as unknown as HTMLElement).style.setProperty(name, value);
};
const px = (n: number): string => `${n}px`;

const setText = (el: Element, text: string): void => {
  if (el.textContent !== text) el.textContent = text;
};

/** the svg's own child layer with this id (a plain walk of the children: jsdom's `:scope >` is not reliable, and this is only ever a handful of nodes) */
const findLayer = (svg: SVGElement, id: string): SVGGElement | null => (Array.from(svg.children).find((c) => c.getAttribute("id") === id) as SVGGElement | undefined) ?? null;

const layer = (svg: SVGElement, id: string, className: string): SVGGElement => {
  let g = findLayer(svg, id);
  if (!g) {
    g = document.createElementNS(NS, "g") as SVGGElement;
    g.setAttribute("id", id);
    g.setAttribute("class", className);
    svg.appendChild(g);
  }

  return g;
};

// ── labels under the rows layout's bubbles ──────────────────────────────────────────────────────────

export interface LabelEntry {
  key: string;
  /** the bubble's own datum: its x / y are what the label follows */
  datum: { x?: number; y?: number };
  name: string;
  /** half the bubble's drawn height: the label hangs below that */
  halfHeight: number;
  /** the room this bubble has along its row */
  room: number;
}

type LabelEl = SVGTextElement & { __datum?: LabelEntry["datum"]; __half?: number };

/** Creates, updates and removes one <text> per bubble so the layer holds exactly `entries`. Positions are set by `positionHistoricalLabels`. */
export const syncHistoricalLabels = (svg: SVGElement, entries: LabelEntry[], measure: Measure, drop: number): void => {
  const g = layer(svg, LABELS_LAYER_ID, "historical-labels");
  const want = new Map(entries.map((e) => [e.key, e]));
  const have = new Map<string, LabelEl>();
  g.querySelectorAll<LabelEl>("text").forEach((t) => {
    const key = t.getAttribute("data-key") || "";
    if (want.has(key) && !have.has(key)) have.set(key, t); else t.remove();
  });
  for (const e of entries) {
    let t = have.get(e.key);
    if (!t) {
      t = document.createElementNS(NS, "text") as LabelEl;
      t.setAttribute("data-key", e.key);
      t.setAttribute("class", "historical-label");
      t.setAttribute("text-anchor", "middle");
      g.appendChild(t);
    }
    t.__datum = e.datum;
    t.__half = e.halfHeight + drop;
    const shown = fitLabel(e.name, labelWidthIn(e.room), measure);
    setText(t, shown);
  }
  positionHistoricalLabels(svg);
};

/** Moves each label to sit under its bubble (called every time the bubbles move: it writes only what changed, to half a pixel). */
export const positionHistoricalLabels = (svg: SVGElement): void => {
  const g = findLayer(svg, LABELS_LAYER_ID);
  if (!g) return;
  g.querySelectorAll<LabelEl>("text").forEach((t) => {
    const d = t.__datum;
    if (!d || typeof d.x !== "number" || typeof d.y !== "number" || Number.isNaN(d.x) || Number.isNaN(d.y)) return;
    setAttr(t, "x", Math.round(d.x * 2) / 2);
    setAttr(t, "y", Math.round((d.y + (t.__half ?? 0)) * 2) / 2);
  });
};

export const removeHistoricalLabels = (svg: SVGElement): void => {
  findLayer(svg, LABELS_LAYER_ID)?.remove();
};

// ── the bar chart ───────────────────────────────────────────────────────────────────────────────────

export interface BarListRow {
  key: string;
  /** the bubble's own datum, put on the row's hit area so the dock's tooltip and click handlers see what they see on a bubble */
  datum: unknown;
  name: string;
  count: number;
  /** the text at the end of the bar: the count and its share */
  valueText: string;
  colour: string;
  iconUrl?: string;
}

export interface ChartHooks {
  onOver: (event: MouseEvent) => void;
  onOut: () => void;
  onClick: (datum: unknown) => void;
  measure: Measure;
}

type BarGroup = SVGGElement & { __parts?: { hit: SVGRectElement; icon: SVGImageElement | null; label: SVGTextElement; bar: SVGRectElement; value: SVGTextElement } };

const makeBar = (hooks: ChartHooks): BarGroup => {
  const group = document.createElementNS(NS, "g") as BarGroup;
  group.setAttribute("class", "hbar");
  const hit = document.createElementNS(NS, "rect") as SVGRectElement;
  hit.setAttribute("class", "hbar-hit");
  hit.setAttribute("fill", "transparent");
  const label = document.createElementNS(NS, "text") as SVGTextElement;
  label.setAttribute("class", "hbar-label");
  label.setAttribute("dominant-baseline", "central");
  const bar = document.createElementNS(NS, "rect") as SVGRectElement;
  bar.setAttribute("class", "hbar-bar");
  bar.setAttribute("rx", "2");
  const value = document.createElementNS(NS, "text") as SVGTextElement;
  value.setAttribute("class", "hbar-value");
  value.setAttribute("dominant-baseline", "central");
  group.append(label, bar, value, hit); // the hit area is LAST so it is on top: it is the one thing the pointer can land on, and it carries the bubble's datum
  group.__parts = { hit, icon: null, label, bar, value };
  hit.addEventListener("mouseover", (event) => hooks.onOver(event));
  hit.addEventListener("mouseout", () => hooks.onOut());
  hit.addEventListener("click", () => hooks.onClick((hit as unknown as { __data__?: unknown }).__data__));

  return group;
};

/**
 * Draws (or updates) the bar chart: one row per entry, positioned by `layout`, the largest bar filling its room. Rows that are no longer in `rows` are removed. A row's group is moved with a CSS
 * transform (animated by the stylesheet, so a row that changes rank glides), and every other attribute is written only when it changed.
 */
export const syncHistoricalBarList = (svg: SVGElement, rows: BarListRow[], layout: BarListLayout, hooks: ChartHooks): void => {
  svg.classList.add(CHART_ON_CLASS);
  const g = layer(svg, LIST_LAYER_ID, "historical-list");
  const want = new Map(rows.map((r) => [r.key, r]));
  const have = new Map<string, BarGroup>();
  (Array.from(g.children) as BarGroup[]).filter((el) => el.classList.contains("hbar")).forEach((el) => {
    const key = el.getAttribute("data-key") || "";
    if (want.has(key) && !have.has(key)) have.set(key, el); else el.remove();
  });
  const largest = Math.max(0, ...rows.map((r) => r.count));
  for (const row of rows) {
    const slot: BarListSlot | undefined = layout.slots.get(row.key);
    if (!slot) continue;
    let group = have.get(row.key);
    if (!group) {
      group = makeBar(hooks);
      group.setAttribute("data-key", row.key);
      g.appendChild(group);
    }
    const parts = group.__parts!;
    (parts.hit as unknown as { __data__?: unknown }).__data__ = row.datum;
    const x = Math.round(slot.x * 2) / 2;
    const y = Math.round(slot.y * 2) / 2;
    const len = barLength(row.count, largest, slot.barMaxW);
    const labelOffset = slot.labelX - slot.x;
    const barOffset = slot.barX - slot.x;
    const iconSize = Math.min(16, layout.pitch - 2);

    setAttr(group, "transform", `translate(${x} ${y})`);
    setAttr(group, "data-count", row.count); // the count the bar shows now (a visitor still flying in is not in it yet)
    setAttr(parts.hit, "x", -6);
    setAttr(parts.hit, "y", -layout.pitch / 2);
    setAttr(parts.hit, "width", slot.colW + 12);
    setAttr(parts.hit, "height", layout.pitch);

    if (row.iconUrl) {
      if (!parts.icon) {
        const icon = document.createElementNS(NS, "image") as SVGImageElement;
        icon.setAttribute("class", "hbar-icon");
        icon.setAttribute("preserveAspectRatio", "xMidYMid meet");
        group.insertBefore(icon, parts.label);
        parts.icon = icon;
      }
      setAttr(parts.icon, "href", row.iconUrl);
      setAttr(parts.icon, "x", 0);
      setAttr(parts.icon, "y", -iconSize / 2);
      setAttr(parts.icon, "width", iconSize);
      setAttr(parts.icon, "height", iconSize);
    } else if (parts.icon) {
      parts.icon.remove();
      parts.icon = null;
    }

    setAttr(parts.label, "x", labelOffset);
    setAttr(parts.label, "y", 0);
    setText(parts.label, fitLabel(row.name, slot.labelW, hooks.measure));
    setAttr(parts.bar, "x", barOffset);
    setAttr(parts.bar, "y", -slot.barH / 2);
    setAttr(parts.bar, "height", slot.barH);
    setAttr(parts.bar, "width", len);
    setStyle(parts.bar, "width", px(len));
    setAttr(parts.bar, "fill", row.colour);
    setAttr(parts.value, "x", 0);
    setAttr(parts.value, "y", 0);
    setStyle(parts.value, "transform", `translate(${px(barOffset + len + BAR_VALUE_GAP)}, 0px)`); // follows the end of the bar
    setText(parts.value, row.valueText);
  }
};

// ── the bar chart of columns (Chris's mock) ─────────────────────────────────────────────────────────

export const COLUMNS_LAYER_ID = "historical-columns";

export interface ColumnRow {
  key: string;
  /** the bubble's own datum, put on the column's hit area (see BarListRow.datum) */
  datum: unknown;
  name: string;
  count: number;
  /** the text over the bar: the share of the whole */
  valueText: string;
  colour: string;
  iconUrl?: string;
}

type ColumnParts = { bar: SVGRectElement; value: SVGTextElement; icon: SVGImageElement | null; label: SVGTextElement; hit: SVGRectElement };
type ColumnGroup = SVGGElement & { __parts?: ColumnParts };

const makeColumn = (hooks: ChartHooks): ColumnGroup => {
  const group = document.createElementNS(NS, "g") as ColumnGroup;
  group.setAttribute("class", "hcol");
  const bar = document.createElementNS(NS, "rect") as SVGRectElement;
  bar.setAttribute("class", "hcol-bar");
  bar.setAttribute("rx", "2");
  const value = document.createElementNS(NS, "text") as SVGTextElement;
  value.setAttribute("class", "hcol-value");
  value.setAttribute("text-anchor", "middle");
  const label = document.createElementNS(NS, "text") as SVGTextElement;
  label.setAttribute("class", "hcol-label");
  label.setAttribute("text-anchor", "middle");
  const hit = document.createElementNS(NS, "rect") as SVGRectElement;
  hit.setAttribute("class", "hcol-hit");
  hit.setAttribute("fill", "transparent");
  group.append(bar, value, label, hit); // the hit area is LAST so it is on top (see makeBar)
  group.__parts = { bar, value, icon: null, label, hit };
  hit.addEventListener("mouseover", (event) => hooks.onOver(event));
  hit.addEventListener("mouseout", () => hooks.onOut());
  hit.addEventListener("click", () => hooks.onClick((hit as unknown as { __data__?: unknown }).__data__));

  return group;
};

/**
 * Draws (or updates) the bar chart of columns: a bar standing on the baseline per category, its share over it and its name (and icon) under it, plus one baseline line and, when some groups were too
 * small to have a column, a line saying how many. Writes only what changed.
 */
export const syncHistoricalColumns = (svg: SVGElement, rows: ColumnRow[], layout: ColumnsLayout, labelZone: number, hooks: ChartHooks, hasIcons: boolean): void => {
  svg.classList.add(CHART_ON_CLASS);
  const g = layer(svg, COLUMNS_LAYER_ID, "historical-columns");
  const want = new Map(rows.map((r) => [r.key, r]));
  const have = new Map<string, ColumnGroup>();
  (Array.from(g.children) as ColumnGroup[]).filter((el) => el.classList.contains("hcol")).forEach((el) => {
    const key = el.getAttribute("data-key") || "";
    if (want.has(key) && layout.slots.has(key) && !have.has(key)) have.set(key, el); else el.remove();
  });
  const holder = (cls: string, tag: string): Element => {
    let el = Array.from(g.children).find((c) => c.classList.contains(cls));
    if (!el) {
      el = document.createElementNS(NS, tag);
      el.setAttribute("class", cls);
      g.appendChild(el);
    }

    return el;
  };
  const axis = holder("hcol-axis", "line");
  setAttr(axis, "x1", Math.round(layout.left * 2) / 2);
  setAttr(axis, "x2", Math.round(layout.right * 2) / 2);
  setAttr(axis, "y1", Math.round(layout.baseline * 2) / 2);
  setAttr(axis, "y2", Math.round(layout.baseline * 2) / 2);
  const more = holder("hcol-more", "text");
  if (layout.hidden > 0) {
    setAttr(more, "text-anchor", "end");
    setAttr(more, "x", Math.round(layout.right * 2) / 2);
    setAttr(more, "y", Math.round((layout.baseline + labelZone + 10) * 2) / 2);
    setText(more, `+ ${layout.hidden.toLocaleString()} smaller ${layout.hidden === 1 ? "group" : "groups"} not shown`);
  } else {
    more.remove();
  }
  const hitTop = layout.barAreaH + COLUMN_METRICS.valueZone;
  for (const row of rows) {
    const slot = layout.slots.get(row.key);
    if (!slot) continue;
    let group = have.get(row.key);
    if (!group) {
      group = makeColumn(hooks);
      group.setAttribute("data-key", row.key);
      g.appendChild(group);
    }
    const parts = group.__parts!;
    (parts.hit as unknown as { __data__?: unknown }).__data__ = row.datum;
    setAttr(group, "transform", `translate(${Math.round(slot.x * 2) / 2} ${Math.round(layout.baseline * 2) / 2})`);
    setAttr(group, "data-count", row.count); // the count the bar shows now (a visitor still flying in is not in it yet)
    setAttr(parts.bar, "x", -slot.barW / 2);
    setAttr(parts.bar, "y", -slot.barH);
    setStyle(parts.bar, "y", px(-slot.barH));
    setAttr(parts.bar, "width", slot.barW);
    setAttr(parts.bar, "height", slot.barH);
    setStyle(parts.bar, "height", px(slot.barH));
    setAttr(parts.bar, "fill", row.colour);
    setAttr(parts.value, "x", 0);
    setAttr(parts.value, "y", 0);
    setStyle(parts.value, "transform", `translate(0px, ${px(-slot.barH - 5)})`); // rides on the top of the bar
    setText(parts.value, row.valueText);
    if (row.iconUrl) {
      if (!parts.icon) {
        const icon = document.createElementNS(NS, "image") as SVGImageElement;
        icon.setAttribute("class", "hcol-icon");
        icon.setAttribute("preserveAspectRatio", "xMidYMid meet");
        group.insertBefore(icon, parts.label);
        parts.icon = icon;
      }
      setAttr(parts.icon, "href", row.iconUrl);
      setAttr(parts.icon, "x", -7);
      setAttr(parts.icon, "y", 5);
      setAttr(parts.icon, "width", 14);
      setAttr(parts.icon, "height", 14);
    } else if (parts.icon) {
      parts.icon.remove();
      parts.icon = null;
    }
    setAttr(parts.label, "x", 0);
    setAttr(parts.label, "y", hasIcons ? 32 : 15);
    setText(parts.label, fitLabel(row.name, labelWidthIn(slot.room), hooks.measure)); // one line, cut with an ellipsis (Chris: "One line cutoff is fine"); the hover card carries the full name
    setAttr(parts.hit, "x", -slot.room / 2);
    setAttr(parts.hit, "y", -hitTop);
    setAttr(parts.hit, "width", slot.room);
    setAttr(parts.hit, "height", hitTop + labelZone);
  }
};

// ── the donut ────────────────────────────────────────────────────────────────────────────

export const DONUT_LAYER_ID = "historical-donut";
export const DONUT_OTHER_COLOUR = "#c8cdd5";

export interface DonutRow {
  key: string;
  /** the bubble's own datum, put on the slice and on its row in the key (see BarListRow.datum) */
  datum: unknown;
  name: string;
  count: number;
  /** the share written beside the name in the key */
  valueText: string;
  colour: string;
  iconUrl?: string;
}

type DonutKeyGroup = SVGGElement & { __parts?: { hit: SVGRectElement; dot: SVGCircleElement; label: SVGTextElement; value: SVGTextElement } };

const donutHooks = (el: Element, hooks: ChartHooks, interactive: boolean): void => {
  if (!interactive) return;
  el.addEventListener("mouseover", (event) => hooks.onOver(event as MouseEvent));
  el.addEventListener("mouseout", () => hooks.onOut());
  el.addEventListener("click", () => hooks.onClick((el as unknown as { __data__?: unknown }).__data__));
};

/**
 * Draws (or updates) the donut: one slice per entry of the layout, the key beside it and the total in the middle. A slice is a dash on a circle (its length and offset are CSS properties, so a slice that grows
 * glides there); the slice and its row in the key both carry the category's datum, so the dock's own hover card and click work on either. Writes only what changed.
 */
export const syncHistoricalDonut = (svg: SVGElement, rows: DonutRow[], layout: DonutLayout, hooks: ChartHooks, centreText: string, otherLabel: string): void => {
  svg.classList.add(CHART_ON_CLASS);
  const g = layer(svg, DONUT_LAYER_ID, "historical-donut");
  if (!(g as unknown as { __hot?: boolean }).__hot) {
    // a slice, its label and its row in the key light up together: hovering any one of them marks all three with `hot`
    (g as unknown as { __hot?: boolean }).__hot = true;
    const mark = (key: string | null, on: boolean) => Array.from(g.children).forEach((el) => { if (key !== null && el.getAttribute("data-key") === key) el.classList.toggle("hot", on); });
    g.addEventListener("mouseover", (event) => mark((((event.target as Element).closest("[data-key]")) as Element | null)?.getAttribute("data-key") ?? null, true));
    g.addEventListener("mouseout", (event) => mark((((event.target as Element).closest("[data-key]")) as Element | null)?.getAttribute("data-key") ?? null, false));
  }
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const C = 2 * Math.PI * layout.rMid;
  const width = layout.R - layout.r;
  const have = new Map<string, SVGCircleElement>();
  Array.from(g.children).filter((el) => el.classList.contains("hdonut-slice")).forEach((el) => {
    const key = el.getAttribute("data-key") || "";
    if (layout.slices.has(key) && !have.has(key)) have.set(key, el as SVGCircleElement); else el.remove();
  });
  const track = (Array.from(g.children).find((c) => c.classList.contains("hdonut-track")) as SVGCircleElement | undefined) ?? (() => {
    const t = document.createElementNS(NS, "circle");
    t.setAttribute("class", "hdonut-track");
    t.setAttribute("fill", "none");
    g.insertBefore(t, g.firstChild);

    return t;
  })();
  setAttr(track, "cx", Math.round(layout.cx * 2) / 2);
  setAttr(track, "cy", Math.round(layout.cy * 2) / 2);
  setAttr(track, "r", layout.rMid);
  setAttr(track, "stroke-width", width);
  layout.slices.forEach((slice, key) => {
    const isOther = key === DONUT_OTHER_ID;
    const row = byKey.get(key);
    let el = have.get(key);
    if (!el) {
      el = document.createElementNS(NS, "circle") as SVGCircleElement;
      el.setAttribute("class", isOther ? "hdonut-slice hdonut-other" : "hdonut-slice");
      el.setAttribute("data-key", key);
      el.setAttribute("fill", "none");
      donutHooks(el, hooks, !isOther);
      g.appendChild(el);
    }
    if (!isOther) (el as unknown as { __data__?: unknown }).__data__ = row?.datum;
    const gap = Math.min(2.5, slice.len * C * 0.2);   // a thin gap between neighbours, never more than a fifth of a slice
    const dash = slice.len > 0 ? Math.max(1.2, slice.len * C - gap) : 0;   // (a very small slice is still a visible sliver)
    setAttr(el, "cx", Math.round(layout.cx * 2) / 2);
    setAttr(el, "cy", Math.round(layout.cy * 2) / 2);
    setAttr(el, "r", layout.rMid);
    setAttr(el, "stroke-width", width);
    setStyle(el, "--hdonut-w", `${width.toFixed(2)}px`);
    setAttr(el, "stroke", isOther ? DONUT_OTHER_COLOUR : row?.colour || DONUT_OTHER_COLOUR);
    setAttr(el, "transform", `rotate(-90 ${Math.round(layout.cx * 2) / 2} ${Math.round(layout.cy * 2) / 2})`);
    setAttr(el, "data-count", row ? row.count : slice.value);
    setStyle(el, "stroke-dasharray", `${dash.toFixed(2)}px ${(C - dash).toFixed(2)}px`);
    setStyle(el, "stroke-dashoffset", `${(-slice.start * C).toFixed(2)}px`);
  });
  // the total in the middle
  const centre = (cls: string): SVGTextElement => {
    let t = Array.from(g.children).find((c) => c.classList.contains(cls)) as SVGTextElement | undefined;
    if (!t) {
      t = document.createElementNS(NS, "text") as SVGTextElement;
      t.setAttribute("class", cls);
      t.setAttribute("text-anchor", "middle");
      g.appendChild(t);
    }

    return t;
  };
  const big = centre("hdonut-total");
  setAttr(big, "x", Math.round(layout.cx * 2) / 2);
  setAttr(big, "y", Math.round((layout.cy + 2) * 2) / 2);
  setText(big, centreText);
  const small = centre("hdonut-total-label");
  setAttr(small, "x", Math.round(layout.cx * 2) / 2);
  setAttr(small, "y", Math.round((layout.cy + 20) * 2) / 2);
  setText(small, "sessions");
  // the key
  const keyHave = new Map<string, DonutKeyGroup>();
  Array.from(g.children).filter((el) => el.classList.contains("hdonut-key")).forEach((el) => {
    const key = el.getAttribute("data-key") || "";
    if (layout.keyRows.some((k) => k.id === key) && !keyHave.has(key)) keyHave.set(key, el as DonutKeyGroup); else el.remove();
  });
  // names on lines around the ring (the Labels option), when the dock is tall enough for them
  const leadHave = new Map<string, SVGGElement>();
  Array.from(g.children).filter((el) => el.classList.contains("hdonut-lead")).forEach((el) => {
    const key = el.getAttribute("data-key") || "";
    if (layout.leads && layout.leads.some((l) => l.id === key) && !leadHave.has(key)) leadHave.set(key, el as SVGGElement); else el.remove();
  });
  (layout.leads || []).forEach((l) => {
    const slice = layout.slices.get(l.id);
    if (!slice) return;
    const row = byKey.get(l.id);
    const isOther = l.id === DONUT_OTHER_ID;
    let group = leadHave.get(l.id);
    if (!group) {
      group = document.createElementNS(NS, "g") as SVGGElement;
      group.setAttribute("class", "hdonut-lead");
      group.setAttribute("data-key", l.id);
      const line = document.createElementNS(NS, "path");
      line.setAttribute("class", "hdonut-leader");
      line.setAttribute("fill", "none");
      const text = document.createElementNS(NS, "text");
      text.setAttribute("class", "hdonut-lead-label");
      text.setAttribute("dominant-baseline", "central");
      donutHooks(text, hooks, !isOther);
      group.append(line, text);
      g.appendChild(group);
    }
    const line = group.children[0] as SVGPathElement;
    const text = group.children[1] as SVGTextElement;
    if (!isOther) (text as unknown as { __data__?: unknown }).__data__ = row?.datum;
    const r5 = (n: number) => Math.round(n * 2) / 2;
    setAttr(line, "d", `M${r5(l.ax)} ${r5(l.ay)}L${r5(l.bx)} ${r5(l.by)}L${r5(l.lx)} ${r5(l.ly)}`);
    setAttr(line, "stroke", isOther ? DONUT_OTHER_COLOUR : row?.colour || DONUT_OTHER_COLOUR);
    const right = l.side === "right";
    setAttr(text, "x", r5(l.lx + (right ? 6 : -6)));
    setAttr(text, "y", r5(l.ly));
    setAttr(text, "text-anchor", right ? "start" : "end");
    const share = isOther ? `${(slice.share * 100).toFixed(1)}%` : row?.valueText ?? "";
    const share_w = hooks.measure(share + "  ");
    setText(text, `${fitLabel(isOther ? otherLabel : row?.name ?? "", Math.max(20, l.room - share_w), hooks.measure)}  ${share}`);
  });
  layout.keyRows.forEach((k) => {
    const row = byKey.get(k.id);
    const isOther = k.id === DONUT_OTHER_ID;
    const slice = layout.slices.get(k.id);
    if (!slice) return;
    let group = keyHave.get(k.id);
    if (!group) {
      group = document.createElementNS(NS, "g") as DonutKeyGroup;
      group.setAttribute("class", "hdonut-key");
      group.setAttribute("data-key", k.id);
      const dot = document.createElementNS(NS, "circle") as SVGCircleElement;
      dot.setAttribute("class", "hdonut-dot");
      dot.setAttribute("r", "5");
      const label = document.createElementNS(NS, "text") as SVGTextElement;
      label.setAttribute("class", "hdonut-key-label");
      label.setAttribute("dominant-baseline", "central");
      const value = document.createElementNS(NS, "text") as SVGTextElement;
      value.setAttribute("class", "hdonut-key-value");
      value.setAttribute("dominant-baseline", "central");
      value.setAttribute("text-anchor", "end");
      const hit = document.createElementNS(NS, "rect") as SVGRectElement;
      hit.setAttribute("class", "hdonut-key-hit");
      hit.setAttribute("fill", "transparent");
      group.append(dot, label, value, hit);
      group.__parts = { hit, dot, label, value };
      donutHooks(hit, hooks, !isOther);
      g.appendChild(group);
    }
    const parts = group.__parts!;
    if (!isOther) (parts.hit as unknown as { __data__?: unknown }).__data__ = row?.datum;
    const x = Math.round(k.x * 2) / 2;
    const y = Math.round(k.y * 2) / 2;
    const textW = 210 - 24 - 56;   // the name's room: the key's width less the dot and the share
    setAttr(group, "transform", `translate(${x} ${y})`);
    setAttr(parts.dot, "cx", 5);
    setAttr(parts.dot, "cy", 0);
    setAttr(parts.dot, "fill", isOther ? DONUT_OTHER_COLOUR : row?.colour || DONUT_OTHER_COLOUR);
    setAttr(parts.label, "x", 18);
    setAttr(parts.label, "y", 0);
    setText(parts.label, fitLabel(isOther ? otherLabel : row?.name ?? "", textW, hooks.measure));
    setAttr(parts.value, "x", 210 - 12);
    setAttr(parts.value, "y", 0);
    setText(parts.value, isOther ? `${(slice.share * 100).toFixed(1)}%` : row?.valueText ?? "");
    setAttr(parts.hit, "x", -4);
    setAttr(parts.hit, "y", -10);
    setAttr(parts.hit, "width", 210 - 4);
    setAttr(parts.hit, "height", 20);
  });
};

export const removeHistoricalCharts = (svg: SVGElement): void => {
  findLayer(svg, LIST_LAYER_ID)?.remove();
  findLayer(svg, COLUMNS_LAYER_ID)?.remove();
  findLayer(svg, DONUT_LAYER_ID)?.remove();
  svg.classList.remove(CHART_ON_CLASS);
};

/**
 * A visitor's bubble has just landed in this category's bar: the bar flashes an outline for a moment. A day of thousands of sessions moves a bar by a fraction of a pixel, so the growth alone cannot
 * be seen; the flash is how the arrival shows at any size. The class is removed and put back a frame later so a second arrival restarts it. Nothing happens for a category with no bar.
 */
export const LANDED_CLASS = "landed";
export const LANDED_MS = 800;
export const pulseChartBar = (svg: SVGElement, key: string): void => {
  const group = Array.from(svg.querySelectorAll("g.hcol, g.hbar, circle.hdonut-slice, g.hdonut-key")).find((g) => g.getAttribute("data-key") === key);
  if (!group) return;
  group.classList.remove(LANDED_CLASS);
  window.requestAnimationFrame(() => {
    group.classList.add(LANDED_CLASS);
    window.setTimeout(() => group.classList.remove(LANDED_CLASS), LANDED_MS);
  });
};
